import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useUpdatePatient } from '@/hooks/usePatients'
import { useToast } from '@/hooks/use-toast'
import { useActivePatient } from '@context/ActivePatientContext'
import type { Patient } from '@/types'

const ginecoSchema = z.object({
  gestas: z.coerce.number().min(0),
  partos: z.coerce.number().min(0),
  cesareas: z.coerce.number().min(0),
  abortos: z.coerce.number().min(0),
  fum: z.string().optional(),
  menarquia_age: z.coerce.number().min(0).max(25).optional().nullable(),
  ciclo_menstrual: z.enum(['regular', 'irregular']),
  anticonceptivos: z.string().max(200).optional(),
})

type GinecoFormValues = z.infer<typeof ginecoSchema>

interface EditGinecoObstetricosDialogProps {
  patient: Patient
  open: boolean
  onOpenChange: (open: boolean) => void
}

function getDefaultValues(patient: Patient): GinecoFormValues {
  const gh = patient.gynecological_history
  return {
    gestas: gh?.gestas ?? 0,
    partos: gh?.partos ?? 0,
    cesareas: gh?.cesareas ?? 0,
    abortos: gh?.abortos ?? 0,
    fum: gh?.fum ?? '',
    menarquia_age: gh?.menarquia_age ?? null,
    ciclo_menstrual: (gh?.ciclo_menstrual as 'regular' | 'irregular') ?? 'regular',
    anticonceptivos: gh?.anticonceptivos ?? '',
  }
}

export function EditGinecoObstetricosDialog({
  patient,
  open,
  onOpenChange,
}: EditGinecoObstetricosDialogProps) {
  const { toast } = useToast()
  const { refreshActivePatient } = useActivePatient()
  const { mutate: updatePatient, isPending } = useUpdatePatient()

  const form = useForm<GinecoFormValues>({
    resolver: zodResolver(ginecoSchema),
    defaultValues: getDefaultValues(patient),
  })

  useEffect(() => {
    if (patient) {
      form.reset(getDefaultValues(patient))
    }
  }, [patient, form])

  function onSubmit(data: GinecoFormValues) {
    updatePatient(
      {
        id: patient.id,
        data: {
          gynecological_history: {
            gestas: data.gestas,
            partos: data.partos,
            cesareas: data.cesareas,
            abortos: data.abortos,
            fum: data.fum || undefined,
            menarquia_age: data.menarquia_age ?? undefined,
            ciclo_menstrual: data.ciclo_menstrual,
            anticonceptivos: data.anticonceptivos || undefined,
          },
        } as any,
      },
      {
        onSuccess: () => {
          refreshActivePatient()
          onOpenChange(false)
          toast({
            title: 'Datos actualizados',
            description: 'Los antecedentes gineco-obstetricos han sido actualizados.',
          })
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message || 'Hubo un error al actualizar los datos.',
            variant: 'destructive',
          })
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Antecedentes Gineco-Obstetricos</DialogTitle>
          <DialogDescription>
            Actualiza los antecedentes ginecologicos y obstetricos de la paciente.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="gestas"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Gestas</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="partos"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Partos</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cesareas"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Cesareas</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="abortos"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Abortos</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="fum"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">FUM (Fecha Ultima Menstruacion)</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="menarquia_age"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">Edad de Menarquia</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      max={25}
                      placeholder="Edad en años"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ciclo_menstrual"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">Ciclo Menstrual</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="regular">Regular</SelectItem>
                      <SelectItem value="irregular">Irregular</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="anticonceptivos"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">Anticonceptivos</FormLabel>
                  <FormControl>
                    <Input placeholder="Metodo anticonceptivo actual" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-ml-primary text-white hover:bg-ml-primary-container"
                disabled={isPending}
              >
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Guardar Cambios
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
